import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useValidityFilter } from "../../../state/FilterContext";
import { useArrayContext } from "../../../state/ArrayContext";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface ValidityPropertyFilterValuesProps {
    parentValidityProp?: string | null,
    children?: ReactNode,
}

/***
 * Array source that returns the current value of a validity filter.
 * The value returned is the value of the first filter in the validity filter
 * after the parent validity filter, which is provided as a prop or as a child.
 */
export const ValidityPropertyFilterValues = React.memo(function ValidityPropertyFilterValues({ parentValidityProp: parentValidityArg, children }: ValidityPropertyFilterValuesProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const validity: string = useValidityFilter();

    // Get parent validity from the validity child unless it was provided as a prop
    const [parentValidity, setParentValidity] = useState<string | null>(null);
    const validityChild = useMemo(() => parentValidityArg ? null : findFirstComponentOfType(children, "arg-validity"), [children, parentValidityArg]);

    useEffect(() => {
        const usedParentValidity = parentValidityArg ?? parentValidity;
        let value = "";
        if (usedParentValidity !== null) {
            if (validity) {
                if (validity === usedParentValidity) {
                    value = "";
                }
                else if (usedParentValidity && validity.startsWith(usedParentValidity + ":")) {
                    value = validity.substring(usedParentValidity.length + 1);
                }
                else {
                    value = validity;
                }
            }
            const endDelimiter = value.indexOf(":");
            if (endDelimiter !== -1) {
                value = value.substring(0, endDelimiter);
            }

            setArray([{
                value: value,
                props: {},
                position: 1,
            }]);
        }
    }, [validity, setArray, parentValidity, parentValidityArg]);

    return <>
        <TextExtractor onExtractedText={setParentValidity}>
            {validityChild?.props.children}
        </TextExtractor>
    </>;
});